import { AgentProfile } from "@closerplatform/spinner-openapi";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { withErrorBoundary } from "@fidget/common/components/errorBoundary";
import { ClickAwayListener } from "@fidget/common/components/form/clickAwayListener";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { IAgent } from "../../../models/agent/Agent";
import { IAgentReferenceType } from "../../../models/agent/types";
import { useAgentServices } from "../../../models/agentServices";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { IRoom } from "../../../models/room/Room";
import { AdviserPicker } from "../../forms/adviserPicker/adviserPicker";
import styles from "./followList.css";

interface Props {
  room: IRoom;
  currentUserStore?: ICurrentUserStore;
}

const FollowListComponent = observer((props: Props) => {
  const { spinnerService } = useAgentServices();
  const [isAddingFollower, setIsAddingFollower] = useState(false);
  const [agents, setAgents] = useState<AgentProfile[]>([]);

  useEffect(() => {
    setIsAddingFollower(() => false);
  }, [props.room]);

  const startAddingFollower = async () => {
    const result = await spinnerService.getActiveAgentProfiles();
    setAgents(result);
    setIsAddingFollower(true);
  };

  const addFollower = async (followerId: string) => {
    await props.room.addNewFollower(followerId);
    setIsAddingFollower(false);
  };

  const filterAgents = () => {
    const { followers }: { followers: Array<IAgentReferenceType> } = props.room;
    const filteredAgents = agents.filter((agent) => agent.id !== props.room.assignee?.id);

    if (!followers.length) {
      return filteredAgents;
    }

    return filteredAgents.filter(
      (agent) => !followers.find((follower: IAgentReferenceType) => follower.id === agent.id)
    );
  };

  const renderNoFollowers = () => (
    <div className={styles["no-followers"]}>
      <L10n id={"conversation.noFollowers"} />
    </div>
  );

  const renderFollowers = () => (
    <div className={styles["followers-container"]}>
      {props.room.followers.map((agent: IAgent) => renderFollower(agent))}
    </div>
  );

  const renderAddFollower = () => (
    <button className={styles["add-follower-plus"]} onClick={startAddingFollower}>
      <i className={"icon-plus"} />
    </button>
  );

  const renderDropdown = () => {
    const filteredAgents = filterAgents();
    return (
      <AdviserPicker
        values={filteredAgents}
        onChange={(agent) => addFollower(agent.id)}
        className={styles["input-follower"]}
        initialIsOpen
        onStateChange={(_, state) => !state.isOpen && setIsAddingFollower(false)}
      />
    );
  };

  const renderFollower = (agent: IAgent) => {
    return (
      <TooltipComponent
        key={agent.id}
        text={agent && agent.displayedName}
        maxWidth={"65px"}
        width={"65px"}
        positionPreset={"top"}
        breakWords
        changeTransformOutset={"translateX(20%)"}
      >
        <div className={styles["follower-avatar"]} key={agent.id}>
          <Avatar agent={agent} />
        </div>
      </TooltipComponent>
    );
  };

  return (
    <>
      {props.room.followers.length === 0 ? renderNoFollowers() : renderFollowers()}
      <ClickAwayListener
        className={styles["add-container"]}
        onClickAway={() => setIsAddingFollower(false)}
      >
        {isAddingFollower ? renderDropdown() : renderAddFollower()}
      </ClickAwayListener>
    </>
  );
});

export const FollowList = withErrorBoundary(FollowListComponent);
